import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  LogIn, ShieldX, Mail, KeyRound, ArrowLeft, Eye, EyeOff, Lock, UserPlus,
  ClipboardCheck, ShieldCheck, Bell, CreditCard, Users, Building2, User,
  Apple, UtensilsCrossed, AlertTriangle, FolderOpen, Briefcase, BellRing,
  MessageSquare, Shield, Check, RefreshCw, Phone, ExternalLink, CalendarClock
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Background image - diverse toddlers playing in daycare
const DAYCARE_BG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ac1ad305-2681-4919-9db2-8590598419fa/images/685a0404489b3cd42fd22229e20470e055d13871674359956df19ee29fe36450.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, completeMfaLogin, resendMfaCode, register, logout } = useAuth();
  
  // Login State
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [securityBlocked, setSecurityBlocked] = useState(false);
  const [blockReasons, setBlockReasons] = useState([]);
  
  // MFA State
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSentTo, setMfaSentTo] = useState('');
  const [mfaChallengeId, setMfaChallengeId] = useState('');
  const [mfaVerifying, setMfaVerifying] = useState(false);
  
  // Password Reset State
  const [resetSent, setResetSent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Register State
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    organizationName: ''
  });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Active tab
  // --- GOOGLE ADS DEFAULT-TAB BEHAVIOR -------------------------------------
  // New visitors arriving from a Google Ads click land on the Register tab
  // first instead of Sign In, since ad clicks are overwhelmingly new
  // prospects, not existing customers. Everyone else (direct visits,
  // bookmarks, typed URLs) is unaffected and still sees Sign In first.
  //
  // Ad-click detection checks two things, since the "Get Started" button on
  // the homepage (a separate site on design.com) drops the ad's "gclid" URL
  // parameter when it links to this page:
  //   1. This page's own URL still has "gclid" or "utm_source=google" (covers
  //      someone linking straight to /login from an ad).
  //   2. A RECENT "_gcl_aw" first-party cookie, written by Google's base tag
  //      already installed on the homepage (design.com) when it sees a
  //      "gclid" — scoped to the whole todayskidzsimplesignin.com domain, so
  //      it's still readable here after "Get Started" is clicked. The cookie
  //      itself can legitimately sit in a browser for up to 90 days, so it is
  //      only honored for a short window after it was set (see
  //      GCL_AW_FRESHNESS_WINDOW_MS below) — long enough to cover homepage ->
  //      "Get Started" -> /login, not long enough to affect someone's next
  //      unrelated visit days or weeks later.
  //
  // TO REVERT: change the useState call below back to:
  //   const [activeTab, setActiveTab] = useState('login');
  // -------------------------------------------------------------------------
  const GCL_AW_FRESHNESS_WINDOW_MS = 30 * 60 * 1000; // 30 minutes

  const hasFreshGclAwCookie = () => {
    const match = document.cookie.split('; ').find((c) => c.startsWith('_gcl_aw='));
    if (!match) return false;
    // Cookie value format: GCL.<unix_seconds_when_set>.<gclid>
    const parts = decodeURIComponent(match.split('=')[1] || '').split('.');
    const setAtMs = parts.length >= 2 ? parseInt(parts[1], 10) * 1000 : NaN;
    if (Number.isNaN(setAtMs)) return false;
    return (Date.now() - setAtMs) <= GCL_AW_FRESHNESS_WINDOW_MS;
  };

  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const hasAdUrlParam = params.has('gclid') || params.get('utm_source') === 'google';
    return (hasAdUrlParam || hasFreshGclAwCookie()) ? 'register' : 'login';
  });

  // Email verification state
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendingVerification, setResendingVerification] = useState(false);

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      await axios.post(`${API}/email-verification/resend`, { email: unverifiedEmail });
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSecurityBlocked(false);
    setBlockReasons([]);
    setEmailNotVerified(false);

    try {
      // Security (IP whitelist, device blocking, geofencing, MFA) is enforced
      // server-side in /auth/login — no frontend pre-check needed.

      // Helpers for the geofencing retry flow
      const getBrowserLocation = () => new Promise((resolve) => {
        if (!navigator.geolocation) { resolve(null); return; }
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
          () => resolve(null),
          { timeout: 10000, enableHighAccuracy: false, maximumAge: 0 }
        );
      });

      const processResult = (result) => {
        // Staff MFA challenge: backend issued a challenge instead of a token
        if (result?.mfa_required) {
          setMfaRequired(true);
          setMfaChallengeId(result.challengeId);
          setMfaSentTo(result.sentTo);
          toast.success(`Verification code sent to ${result.sentTo}`);
          setLoading(false);
          return;
        }

        if (result?.user?.role === 'parent') {
          logout();
          toast.error(
            <span>
              This is the center sign-in. Parents, please log in at the{' '}
              <a href="/parent/login" className="underline font-semibold">Parent Portal</a>.
            </span>,
            { duration: 8000 }
          );
          return;
        }

        navigate('/');
      };

      // First attempt without location; only geofenced staff will be asked for it.
      let result;
      try {
        result = await login(loginData.email, loginData.password);
      } catch (err) {
        if (err.response?.status === 403 && err.response?.data?.detail?.locationRequired) {
          const coords = await getBrowserLocation();
          if (!coords) {
            toast.error('Location access is required to sign in. Please enable location in your browser and try again.', { duration: 7000 });
            setLoading(false);
            return;
          }
          result = await login(loginData.email, loginData.password, coords.latitude, coords.longitude);
        } else {
          throw err;
        }
      }

      processResult(result);
    } catch (error) {
      console.error('Login error:', error);
      
      const errorDetail = error.response?.data?.detail;
      
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        const { message, reset_sent, locked, minutes_remaining, email_not_verified, email } = errorDetail;
        
        // Handle email not verified
        if (email_not_verified) {
          setEmailNotVerified(true);
          setUnverifiedEmail(email || loginData.email);
          toast.error('Please verify your email before logging in.', { duration: 5000 });
        } else if (locked || reset_sent) {
          setResetSent(true);
          if (locked) {
            toast.error(`Account locked for ${minutes_remaining || 30} minutes. Check email for reset link.`, { duration: 8000 });
          } else {
            toast.info('Password reset link sent to your email', { duration: 5000 });
          }
        } else if (Array.isArray(errorDetail.blockedReasons) && errorDetail.blockedReasons.length) {
          setSecurityBlocked(true);
          setBlockReasons(errorDetail.blockedReasons);
          toast.error(message || 'Access denied by security restrictions');
        } else {
          toast.error(message);
        }
      } else {
        toast.error(errorDetail || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setMfaVerifying(true);

    try {
      const result = await completeMfaLogin(mfaChallengeId, mfaCode);

      if (result?.user?.role === 'parent') {
        logout();
        toast.error(
          <span>
            This is the center sign-in. Parents, please log in at the{' '}
            <a href="/parent/login" className="underline font-semibold">Parent Portal</a>.
          </span>,
          { duration: 8000 }
        );
        return;
      }

      navigate('/');
    } catch (error) {
      console.error('MFA verification error:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setMfaVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const result = await resendMfaCode(mfaChallengeId);
      toast.success(`New code sent to ${result.sentTo}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaBack = () => {
    setMfaRequired(false);
    setMfaCode('');
    setMfaSentTo('');
    setMfaChallengeId('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setRegisterLoading(true);

    try {
      await register(registerData.email, registerData.password, registerData.name, registerData.organizationName, registerData.phone);
      // Fire the Google Ads "Sign-up" conversion exactly once, right here where a
      // brand-new center registration via the /login Register tab has actually
      // just succeeded — same conversion ID/label used on the /register page.
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL,
          'value': 1.0,
          'currency': 'USD'
        });
      }
      toast.success('Account created successfully! Welcome to ToDays Kidz!');
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Header - Logo Only, Centered with slight offset to show full logo */}
      <header className="w-full px-4 sm:px-6 lg:px-8 py-2 flex justify-center items-center absolute top-0 left-0 right-0 z-30">
        <div className="flex flex-col items-center pr-10 sm:pr-12">
          <img 
            src="/simple-sign-in-logo.svg" 
            alt="ToDays Kidz Logo" 
            className="h-36 sm:h-40 md:h-48 w-auto object-contain drop-shadow-lg"
            style={{ filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }}
            data-testid="header-logo"
          />
        </div>
      </header>

      {/* Main Content Area with Background Image */}
      <div className="flex-grow flex flex-col relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-no-repeat"
          style={{ 
            backgroundImage: `url(${DAYCARE_BG_IMAGE})`,
            backgroundPosition: 'center 30%'
          }}
        />
        {/* Gradient overlay for better readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40" />

        {/* Content Container */}
        <div className="relative z-10 flex-grow flex flex-col items-center justify-center px-4 pt-44 sm:pt-48 pb-6">
          {/* Tagline */}
          <div className="text-center mb-4 px-4">
            <h1 
              className="text-xl sm:text-2xl md:text-3xl font-bold drop-shadow-lg"
              style={{ color: 'rgb(39,165,220)', textShadow: '2px 2px 8px rgba(0,0,0,0.7)' }}
              data-testid="main-tagline"
            >
              Where Safety Meets Simplicity
            </h1>
            <p 
              className="text-base sm:text-lg md:text-xl font-semibold mt-0.5 drop-shadow-lg"
              style={{ color: 'rgb(39,165,220)', textShadow: '2px 2px 6px rgba(0,0,0,0.6)' }}
            >
              Childcare Safety & Compliance Platform
            </p>
            <p 
              className="text-base sm:text-lg mt-0.5 font-semibold drop-shadow-lg italic"
              style={{ color: 'white', textShadow: '0 0 10px rgba(39,165,220,0.8), 0 0 20px rgba(39,165,220,0.6), 2px 2px 8px rgba(0,0,0,0.8)' }}
            >
              Built by a Childcare Owner for Childcare Providers
            </p>
          </div>

          {/* Auth Card - Smaller */}
          <Card 
            className="w-full max-w-xs bg-white/95 backdrop-blur-xl border-0 shadow-2xl rounded-2xl overflow-hidden"
            data-testid="auth-card"
          >
            <CardContent className="p-4 sm:p-5">
              {/* MFA Verification Screen */}
              {mfaRequired ? (
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <h2 className="text-xl font-bold text-[#1E293B]">Verify Your Identity</h2>
                    <p className="text-[#64748B] text-sm mt-1">Enter the code sent to your email</p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 border-2 border-[#4DA3D9] rounded-lg text-center">
                    <Mail className="w-10 h-10 text-[#2F6DB5] mx-auto mb-2" />
                    <p className="text-[#1E293B] font-medium">Verification Code Sent</p>
                    <p className="text-[#64748B] text-sm">
                      We've sent a 6-digit code to <span className="font-semibold">{mfaSentTo}</span>
                    </p>
                  </div>

                  <form onSubmit={handleMfaVerify} className="space-y-4">
                    <div>
                      <Label htmlFor="mfaCode" className="text-[#1E293B]">Enter Verification Code</Label>
                      <Input
                        id="mfaCode"
                        type="text"
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        required
                        maxLength={6}
                        className="border-[#E2E8F0] focus:border-[#2F6DB5] text-center text-2xl tracking-widest h-12"
                        data-testid="mfa-code-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#2F6DB5] hover:bg-[#255A96] text-white h-11"
                      disabled={mfaVerifying || mfaCode.length !== 6}
                      data-testid="mfa-verify-btn"
                    >
                      {mfaVerifying ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <KeyRound className="w-5 h-5 mr-2" />
                          Verify Code
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={handleMfaBack}
                      className="text-[#2F6DB5] hover:text-[#E83E8C] flex items-center gap-1"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to login
                    </button>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      className="text-[#2F6DB5] hover:text-[#E83E8C]"
                      disabled={loading}
                    >
                      {loading ? 'Sending...' : 'Resend code'}
                    </button>
                  </div>
                </div>
              ) : resetSent ? (
                /* Account Locked Message */
                <div className="space-y-4">
                  <div className="p-4 bg-red-50 border-2 border-red-400 rounded-lg">
                    <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                      <ShieldX className="w-5 h-5" />
                      Account Locked
                    </div>
                    <p className="text-sm text-red-600 mb-3">
                      After 5 failed login attempts, your account has been temporarily locked for security.
                    </p>
                    <p className="text-sm text-red-600 mb-3">
                      A password reset link has been sent to <strong>{loginData.email}</strong>. Please check your email.
                    </p>
                    <p className="text-xs text-gray-500 mb-3">
                      The lockout will automatically expire after 30 minutes.
                    </p>
                    <Button
                      variant="outline"
                      className="w-full border-red-400 text-red-700 hover:bg-red-100"
                      onClick={() => setResetSent(false)}
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Try Again
                    </Button>
                  </div>
                </div>
              ) : (
                /* Tabs for Login / Register */
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="w-full grid grid-cols-2 bg-transparent border-b border-[#E2E8F0] rounded-none h-auto p-0 mb-6">
                    <TabsTrigger 
                      value="login" 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2F6DB5] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#2F6DB5] text-[#64748B] py-3 font-semibold text-base"
                      data-testid="login-tab"
                    >
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger 
                      value="register" 
                      className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#2F6DB5] data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-[#2F6DB5] text-[#64748B] py-3 font-semibold text-base"
                      data-testid="register-tab"
                    >
                      Register
                    </TabsTrigger>
                  </TabsList>

                  {/* Security Blocked Warning */}
                  {securityBlocked && (
                    <div className="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-lg">
                      <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                        <ShieldX className="w-5 h-5" />
                        Access Denied
                      </div>
                      <ul className="text-sm text-red-600 list-disc list-inside">
                        {blockReasons.map((reason, idx) => (
                          <li key={idx}>{reason}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Email Not Verified Warning */}
                  {emailNotVerified && (
                    <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-700 font-semibold mb-2">
                        <Mail className="w-5 h-5" />
                        Email Verification Required
                      </div>
                      <p className="text-sm text-blue-700 mb-3">
                        Please check your inbox for the verification link we sent to <strong>{unverifiedEmail}</strong>.
                        If you don't see it within a few minutes, please also check your <strong>Junk/Spam folder</strong> — it's sometimes delivered there.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleResendVerification}
                          disabled={resendingVerification}
                          className="border-blue-400 text-blue-700 hover:bg-blue-100"
                        >
                          {resendingVerification ? (
                            <>
                              <Mail className="w-4 h-4 mr-1 animate-pulse" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Mail className="w-4 h-4 mr-1" />
                              Resend Verification Email
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Login Tab Content */}
                  <TabsContent value="login" className="mt-0">
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="login-email" className="text-[#1E293B] font-medium text-sm">Email</Label>
                        <div className="relative group">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                          <Input
                            id="login-email"
                            type="email"
                            value={loginData.email}
                            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                            placeholder="your@email.com"
                            required
                            className="pl-10 h-11 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-lg bg-white"
                            data-testid="login-email"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="login-password" className="text-[#1E293B] font-medium text-sm">Password</Label>
                        <div className="relative group">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                          <Input
                            id="login-password"
                            type={showPassword ? "text" : "password"}
                            value={loginData.password}
                            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                            placeholder="Enter your password"
                            required
                            className="pl-10 pr-10 h-11 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-lg bg-white"
                            data-testid="login-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#2F6DB5] transition-colors"
                            tabIndex={-1}
                          >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                        <div className="flex justify-end">
                          <Link 
                            to="/forgot-password" 
                            className="text-sm text-[#2F6DB5] hover:text-[#E83E8C] font-medium transition-colors"
                            data-testid="forgot-password-link"
                          >
                            Forgot Password?
                          </Link>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-11 bg-[#2F6DB5] hover:bg-[#255A96] text-white font-semibold rounded-lg shadow-md"
                        data-testid="login-submit"
                      >
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Signing in...
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <LogIn className="w-5 h-5" />
                            Sign In
                          </div>
                        )}
                      </Button>
                    </form>
                  </TabsContent>

                  {/* Register Tab Content */}
                  <TabsContent value="register" className="mt-0">
                    <form onSubmit={handleRegister} className="space-y-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="register-org" className="text-[#1E293B] font-medium text-sm">Center Name</Label>
                        <div className="relative group">
                          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                          <Input
                            id="register-org"
                            value={registerData.organizationName}
                            onChange={(e) => setRegisterData({ ...registerData, organizationName: e.target.value })}
                            placeholder="Little Stars Daycare"
                            required
                            className="pl-10 h-10 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-lg bg-white"
                            data-testid="register-org-name"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="register-name" className="text-[#1E293B] font-medium text-sm">Your Name</Label>
                        <div className="relative group">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                          <Input
                            id="register-name"
                            value={registerData.name}
                            onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                            placeholder="John Smith"
                            required
                            className="pl-10 h-10 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-lg bg-white"
                            data-testid="register-name"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="register-email" className="text-[#1E293B] font-medium text-sm">Email</Label>
                        <div className="relative group">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                          <Input
                            id="register-email"
                            type="email"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            placeholder="director@daycare.com"
                            required
                            className="pl-10 h-10 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-lg bg-white"
                            data-testid="register-email"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="register-phone" className="text-[#1E293B] font-medium text-sm">Mobile Phone</Label>
                        <div className="relative group">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                          <Input
                            id="register-phone"
                            type="tel"
                            value={registerData.phone}
                            onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                            placeholder="(555) 123-4567"
                            className="pl-10 h-10 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-lg bg-white"
                            data-testid="register-phone"
                          />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="register-password" className="text-[#1E293B] font-medium text-sm">Password</Label>
                        <div className="relative group">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                          <Input
                            id="register-password"
                            type={showRegisterPassword ? "text" : "password"}
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            placeholder="At least 6 characters"
                            required
                            className="pl-10 pr-10 h-10 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-lg bg-white"
                            data-testid="register-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#2F6DB5] transition-colors"
                            tabIndex={-1}
                          >
                            {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="register-confirm" className="text-[#1E293B] font-medium text-sm">Confirm Password</Label>
                        <div className="relative group">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] group-focus-within:text-[#2F6DB5] transition-colors" />
                          <Input
                            id="register-confirm"
                            type={showConfirmPassword ? "text" : "password"}
                            value={registerData.confirmPassword}
                            onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                            placeholder="Re-enter password"
                            required
                            className="pl-10 pr-10 h-10 border-[#E2E8F0] focus:border-[#2F6DB5] focus:ring-2 focus:ring-[#2F6DB5]/20 rounded-lg bg-white"
                            data-testid="register-confirm-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#2F6DB5] transition-colors"
                            tabIndex={-1}
                          >
                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={registerLoading}
                        className="w-full h-11 bg-[#22C55E] hover:bg-[#16A34A] text-white font-semibold rounded-lg shadow-md mt-2"
                        data-testid="register-submit"
                      >
                        {registerLoading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Creating account...
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5" />
                            Start Free Trial
                          </div>
                        )}
                      </Button>

                      <p className="text-xs text-center text-[#64748B] mt-2">
                        By creating an account, you agree to our{' '}
                        <a href="https://todayskidzsimplesignin.com/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-[#2F6DB5] hover:underline">
                          Terms
                        </a>
                        {' & '}
                        <a href="https://todayskidzsimplesignin.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#2F6DB5] hover:underline">
                          Privacy Policy
                        </a>
                      </p>

                      <p className="text-xs text-center text-[#64748B] mt-1" data-testid="register-switch-to-signin">
                        Already have an account?{' '}
                        <button
                          type="button"
                          onClick={() => setActiveTab('login')}
                          className="text-[#2F6DB5] hover:underline font-medium"
                        >
                          Sign in here
                        </button>
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Features Section - At Bottom */}
      <div className="w-full bg-white py-6 border-t border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-4">
          {/* Row 1 - Priority Features */}
          <div className="flex flex-wrap justify-center items-start gap-4 sm:gap-6 mb-4">
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <Shield className="w-7 h-7 sm:w-8 sm:h-8 text-[#059669] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Secure Pickup Verification</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <AlertTriangle className="w-7 h-7 sm:w-8 sm:h-8 text-[#F4A623] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Secure Pickup Alerts</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <BellRing className="w-7 h-7 sm:w-8 sm:h-8 text-[#E83E8C] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Real-Time Staff Alerts</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <MessageSquare className="w-7 h-7 sm:w-8 sm:h-8 text-[#2563EB] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Instant SMS Notifications</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <ClipboardCheck className="w-7 h-7 sm:w-8 sm:h-8 text-[#2F6DB5] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Daily Sign-In</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <CalendarClock className="w-7 h-7 sm:w-8 sm:h-8 text-[#0D9488] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Center Tour Scheduling & Live Ratio Compliance</span>
            </div>
            {/* Meal Compliance Tracker - with sub-features inline */}
            <div className="flex flex-col items-center gap-1">
              <div className="flex items-center gap-2">
                <Apple className="w-7 h-7 sm:w-8 sm:h-8 text-[#DC2626]" strokeWidth={1.5} />
                <span className="text-[10px] sm:text-xs text-[#DC2626] font-semibold">Meal Compliance Tracker</span>
              </div>
              <div className="flex flex-col gap-0.5 text-[9px] sm:text-[10px] text-[#64748B]">
                <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#22C55E]" />Staff accountability (with icon tracking)</span>
                <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#22C55E]" />Attendance-based meal validation</span>
                <span className="flex items-center gap-1"><Check className="w-3 h-3 text-[#22C55E]" />Printable reports for CACFP</span>
              </div>
            </div>
          </div>
          
          {/* Row 2 - Additional Features */}
          <div className="flex flex-wrap justify-center items-start gap-4 sm:gap-6">
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <UtensilsCrossed className="w-7 h-7 sm:w-8 sm:h-8 text-[#16A34A] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Center Menu Uploads</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <FolderOpen className="w-7 h-7 sm:w-8 sm:h-8 text-[#8B5CF6] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Parent Document Portal</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <FolderOpen className="w-7 h-7 sm:w-8 sm:h-8 text-[#7C3AED] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Compliance Document Center</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <Users className="w-7 h-7 sm:w-8 sm:h-8 text-[#DB2777] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Staff Compliance Tracker</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <Briefcase className="w-7 h-7 sm:w-8 sm:h-8 text-[#0891B2] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Payroll Hub</span>
            </div>
            <div className="flex flex-col items-center gap-2 group cursor-default">
              <CreditCard className="w-7 h-7 sm:w-8 sm:h-8 text-[#4DA3D9] group-hover:scale-110 transition-transform" strokeWidth={1.5} />
              <span className="text-[10px] sm:text-xs text-[#1E293B] font-medium text-center leading-tight">Billing</span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with Twilio Compliance Links */}
      <footer className="w-full bg-[#F8FAFC] py-6 border-t border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4">
          {/* Questions Contact */}
          <p className="text-center text-base sm:text-lg text-[#64748B] mb-6">
            Questions? Contact{' '}
            <a 
              href="mailto:admin@todayskidzsimplesignin.com"
              className="text-[#2F6DB5] font-semibold hover:underline"
            >
              admin@todayskidzsimplesignin.com
            </a>
          </p>

          {/* Cross-promotion: ToDays Kidz Tax */}
          <a
            href="https://todayskidztax.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block mx-auto max-w-lg mb-6 group"
            data-testid="todayskidztax-promo-link"
          >
            <div className="flex items-center gap-4 bg-[#DBEAFE] border-l-[5px] border-[#1D4ED8] rounded-xl px-5 py-4 shadow-sm transition-all group-hover:bg-[#BFDBFE] group-hover:shadow-md">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#1E3A8A] leading-snug tracking-wide uppercase">
                  Need help with childcare taxes?
                </p>
                <p className="text-base text-[#1D4ED8] font-semibold mt-1">
                  Explore ToDays Kidz Tax →
                </p>
                <p className="text-xs text-[#475569] mt-0.5 font-medium">
                  Built specifically for childcare centers.
                </p>
              </div>
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-[#1D4ED8] flex items-center justify-center group-hover:bg-[#1E40AF] transition-colors">
                <ExternalLink className="w-4 h-4 text-white" />
              </div>
            </div>
          </a>

          {/* Divider */}
          <div className="border-t border-[#E2E8F0] mb-6"></div>
          
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 text-sm sm:text-base text-[#2F6DB5] font-medium">
            <a 
              href="https://todayskidzsimplesignin.com/terms-and-conditions" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Terms & Conditions
            </a>
            <span className="text-[#CBD5E1]">·</span>
            <a 
              href="https://todayskidzsimplesignin.com/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline"
            >
              Privacy Policy
            </a>
            <span className="text-[#CBD5E1]">·</span>
            <a 
              href="https://todayskidzsimplesignin.com/sms-consent-enrollment" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline"
            >
              SMS Consent
            </a>
          </div>
          
          {/* Copyright */}
          <p className="text-center text-xs text-[#94A3B8] mt-4">
            © {new Date().getFullYear()} ToDays Kidz Simple Sign In. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LoginPage;
